import { useState } from 'react';
import { usePwa } from '@/components/feature/PwaProvider';
import { detectPlatform } from '@/lib/pushDevices';

interface Props {
  /** Contexte d'affichage (adapte le texte). */
  variant?: 'client' | 'merchant';
  className?: string;
}

type DeviceKind = 'ios' | 'android' | 'desktop';

/** Détecte précisément l'appareil : iPhone/iPad, Android, ou ordinateur. */
function detectDevice(): DeviceKind {
  if (typeof navigator === 'undefined') return 'desktop';
  const ua = navigator.userAgent;
  const platform = detectPlatform();
  if (platform === 'ios') return 'ios';
  if (platform === 'android') return 'android';
  // iPad récent qui se présente comme Mac + écran tactile.
  if (/Macintosh/.test(ua) && typeof document !== 'undefined' && 'ontouchend' in document) return 'ios';
  return 'desktop';
}

const DEVICE_LABEL: Record<DeviceKind, string> = {
  ios: 'iPhone / iPad',
  android: 'Android',
  desktop: 'Ordinateur',
};

/**
 * Carte « Installer l'application » affichée dans les espaces (compte client
 * et dashboard commerçant).
 *
 * • Détecte l'appareil (iPhone, Android, ordinateur).
 * • Android / ordinateur : « Installer maintenant » lance DIRECTEMENT
 *   l'installation PWA puis demande l'abonnement aux notifications.
 * • iPhone : affiche la procédure « Sur l'écran d'accueil » ET propose
 *   l'abonnement aux notifications.
 */
export default function InstallAppCard({ variant = 'merchant', className = '' }: Props) {
  const { installed, canInstall, install, notifications, enableNotifications, testNotification, badgeCount, deviceRegistered } = usePwa();
  const [guide, setGuide] = useState<DeviceKind | null>(null);
  const [testing, setTesting] = useState(false);
  const [testMsg, setTestMsg] = useState<string | null>(null);

  const device = detectDevice();
  const notifGranted = notifications === 'granted';
  const notifBlocked = notifications === 'denied';

  const handleTest = async () => {
    setTesting(true);
    setTestMsg(null);
    try {
      await testNotification();
      setTestMsg('Notification de test envoyée — regardez votre écran.');
    } catch {
      setTestMsg('Impossible d\u2019envoyer la notification pour le moment.');
    } finally {
      setTesting(false);
      setTimeout(() => setTestMsg(null), 5000);
    }
  };

  // Clic sur « Installer maintenant ».
  const handleInstall = async () => {
    // iPhone : pas de prompt natif → on montre la procédure, tout en
    // proposant l'abonnement aux notifications.
    if (device === 'ios') {
      setGuide('ios');
      return;
    }
    // Android / ordinateur : on lance l'installation, puis on demande
    // l'abonnement aux notifications (dans la foulée).
    await install();
    await enableNotifications();
    // Si le navigateur n'a pas fourni de prompt d'installation, on guide.
    if (!canInstall) setGuide('desktop');
  };

  // Abonnement aux notifications depuis la procédure iPhone / le guide desktop.
  const subscribeFromGuide = async () => {
    await enableNotifications();
  };

  const subtitle = variant === 'client'
    ? 'Retrouvez votre compte comme une vraie application et recevez vos alertes.'
    : 'Gérez votre commerce comme une vraie application, même hors connexion.';

  return (
    <div className={`bg-background-50 border border-background-200/50 rounded-xl p-5 ${className}`}>
      <div className="flex items-start gap-4">
        <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${installed ? 'bg-secondary-100 text-secondary-700' : 'bg-primary-100 text-primary-700'}`}>
          <i className={`text-xl ${installed ? 'ri-checkbox-circle-line' : device === 'ios' ? 'ri-apple-line' : device === 'android' ? 'ri-android-line' : 'ri-computer-line'}`}></i>
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="text-sm font-bold font-heading text-foreground-950">
              {installed ? 'Application installée' : 'Installer l\u2019application'}
            </h3>
            {installed && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-secondary-100 text-secondary-700">
                <i className="ri-check-line"></i>Active
              </span>
            )}
            {badgeCount > 0 && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-primary-500 text-background-50">
                <i className="ri-notification-3-line"></i>{badgeCount} non lue{badgeCount > 1 ? 's' : ''}
              </span>
            )}
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-background-100 text-foreground-500">
              <i className="ri-device-line"></i>{DEVICE_LABEL[device]}
            </span>
          </div>
          <p className="text-xs text-foreground-500 mt-1 leading-snug">{subtitle}</p>

          <div className="flex flex-wrap items-center gap-2 mt-3">
            {!installed && (
              <button
                onClick={handleInstall}
                className="h-9 px-4 rounded-full bg-primary-500 text-background-50 text-xs font-semibold whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer flex items-center gap-1.5"
              >
                <i className={device === 'ios' ? 'ri-apple-line' : 'ri-download-2-line'}></i>
                {device === 'ios' ? 'Installer sur iPhone' : 'Installer maintenant'}
              </button>
            )}

            {!notifGranted && notifications !== 'unsupported' && (
              <button
                onClick={enableNotifications}
                className="h-9 px-4 rounded-full border border-accent-300 text-accent-700 text-xs font-semibold whitespace-nowrap hover:bg-accent-50 transition-colors cursor-pointer flex items-center gap-1.5"
              >
                <i className="ri-notification-3-line"></i>
                {notifBlocked ? 'Notifications bloquées' : 'Activer les notifications'}
              </button>
            )}

            {notifGranted && (
              <button
                onClick={handleTest}
                disabled={testing}
                className="h-9 px-4 rounded-full border border-secondary-300 text-secondary-700 text-xs font-semibold whitespace-nowrap hover:bg-secondary-50 disabled:opacity-50 transition-colors cursor-pointer flex items-center gap-1.5"
              >
                <i className={testing ? 'ri-loader-4-line animate-spin' : 'ri-notification-badge-line'}></i>
                Tester la notification
              </button>
            )}

            {device !== 'ios' && !installed && (
              <button
                onClick={() => setGuide('ios')}
                className="h-9 px-3 rounded-full text-[11px] font-medium text-foreground-500 hover:text-primary-600 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
              >
                Vous êtes sur iPhone ?
              </button>
            )}

            {deviceRegistered && (
              <span className="inline-flex items-center gap-1.5 text-[11px] text-foreground-400">
                <i className="ri-device-line"></i>
                Appareil enregistré
              </span>
            )}
          </div>

          {testMsg && (
            <p className="mt-2.5 text-[11px] text-secondary-700 flex items-center gap-1.5">
              <i className="ri-checkbox-circle-line"></i>
              {testMsg}
            </p>
          )}
        </div>
      </div>

      {guide && (
        <>
          <div className="fixed inset-0 bg-black/40 z-[70]" onClick={() => setGuide(null)} />
          <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-[70] w-[calc(100vw-2rem)] max-w-sm bg-background-50 rounded-2xl border border-background-200/70 p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-11 h-11 rounded-xl bg-primary-100 text-primary-600 flex items-center justify-center flex-shrink-0">
                <i className={`text-xl ${guide === 'ios' ? 'ri-apple-line' : 'ri-smartphone-line'}`}></i>
              </div>
              <div>
                <h3 className="text-sm font-bold text-foreground-950">Installer l&apos;application</h3>
                <p className="text-xs text-foreground-500 mt-0.5">
                  {guide === 'ios' ? 'Sur iPhone / iPad (Safari ou Chrome).' : 'Depuis votre navigateur.'}
                </p>
              </div>
            </div>

            {guide === 'ios' ? (
              <div className="space-y-3 text-sm text-foreground-700">
                <div className="flex items-start gap-2.5">
                  <i className="ri-share-forward-line text-lg text-accent-600 mt-0.5"></i>
                  <p><strong>1.</strong> Appuyez sur <strong>Partager</strong> <i className="ri-share-forward-line"></i> en bas de l&apos;écran.</p>
                </div>
                <div className="flex items-start gap-2.5">
                  <i className="ri-add-box-line text-lg text-accent-600 mt-0.5"></i>
                  <p><strong>2.</strong> Choisissez <strong>« Sur l&apos;écran d&apos;accueil »</strong>, puis <strong>Ajouter</strong>.</p>
                </div>
                <div className="flex items-start gap-2.5">
                  <i className="ri-notification-3-line text-lg text-accent-600 mt-0.5"></i>
                  <p><strong>3.</strong> Ouvrez l&apos;application, puis <strong>abonnez-vous aux notifications</strong> pour recevoir les commandes, messages et réservations.</p>
                </div>
              </div>
            ) : (
              <div className="space-y-3 text-sm text-foreground-700">
                <div className="flex items-start gap-2.5">
                  <i className="ri-more-2-line text-lg text-accent-600 mt-0.5"></i>
                  <p><strong>1.</strong> Ouvrez le menu du navigateur (icône <strong>⋮</strong> ou <strong>⋮⋮</strong>).</p>
                </div>
                <div className="flex items-start gap-2.5">
                  <i className="ri-download-2-line text-lg text-accent-600 mt-0.5"></i>
                  <p><strong>2.</strong> Choisissez <strong>« Installer l&apos;application »</strong> / <strong>« Créer un raccourci »</strong>.</p>
                </div>
                <div className="flex items-start gap-2.5">
                  <i className="ri-notification-3-line text-lg text-accent-600 mt-0.5"></i>
                  <p><strong>3.</strong> Confirmez, puis <strong>abonnez-vous aux notifications</strong> ci-dessous.</p>
                </div>
              </div>
            )}

            {!notifGranted && notifications !== 'unsupported' && (
              <button
                onClick={subscribeFromGuide}
                className="mt-5 w-full h-11 rounded-full border border-accent-300 text-accent-700 text-sm font-semibold whitespace-nowrap hover:bg-accent-50 transition-colors cursor-pointer flex items-center justify-center gap-2"
              >
                <i className="ri-notification-3-line"></i>
                S&apos;abonner aux notifications
              </button>
            )}

            {notifGranted && (
              <p className="mt-4 text-xs text-secondary-700 flex items-center gap-1.5">
                <i className="ri-checkbox-circle-line"></i>
                Notifications déjà activées sur cet appareil.
              </p>
            )}

            <button
              onClick={() => setGuide(null)}
              className="mt-3 w-full h-11 rounded-full bg-primary-500 text-background-50 text-sm font-semibold whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer"
            >
              Compris
            </button>
          </div>
        </>
      )}
    </div>
  );
}